import { Factory, Model, type Collection } from 'miragejs'
import type { ModelDefinition, ModelInstance } from 'miragejs/-types'
import {
  rand,
  randNumber,
  randFloat,
  randHex,
  randWord,
  randPhrase,
  randParagraph,
  randBoolean,
  randFullName,
  randPastDate,
} from '@ngneat/falso'
import { sentenceCase } from 'change-case'

import type { MockServer, MirageServer } from './server.js'
import type { DepartmentData } from './Department.js'
import { pageable, sortable } from './utils.js'
import { formatDate } from '@/utils/date/conversion.js'
import { safeMathRandom } from '@/utils/number.js'
import type { RevisionLogData } from '@/composables/revisionLogs.js'
import {
  useCurrentLocaleDateFormat,
  useCurrentLocaleTimeFormat,
} from '@/composables/i18next.js'

export const model = Model as ModelDefinition<RevisionLogData>

enum Fields {
  Number = 'Number',
  Date = 'Date',
  Boolean = 'Boolean',
  Tags = 'Tags',
  Text = 'Text',
  Notes = 'Notes',
}

/**
 * Returns a string representation of a random date in the format of:
 * - ISO 8601 date string
 * - Formatted date string in the format of DD/MM/YYYY HH:mm
 * - Formatted date string in the format of DD/MM/YYYY hh:mm a.m./p.m. (with lowercase am/pm)
 *
 * @returns {string} A string representation of a random date
 */
function getDate(): string {
  const date = randPastDate().toISOString()
  const formattedDateString = `${formatDate(date, useCurrentLocaleDateFormat())} ${formatDate(date, useCurrentLocaleTimeFormat())}`
  const lowercasedDate = formattedDateString
    .replace('AM', rand(['am', 'a.m.']))
    .replace('PM', rand(['pm', 'p.m.']))

  return rand([date, formattedDateString, lowercasedDate])
}

function getTags(): string {
  return Array.from({
    length: randNumber({ min: 1, max: 10 }),
  })
    .map(() => sentenceCase(randPhrase({ maxCharCount: 60 })))
    .join(', ')
}

function generateValue(
  field: string,
  {
    allowNull,
    excludeValue,
  }: { allowNull?: boolean; excludeValue?: string | null } = {
    allowNull: true,
  },
): string | null {
  const initialValues = allowNull ? [null] : []

  switch (field) {
    case Fields.Number:
      return rand([
        ...initialValues,
        randNumber().toString(),
        randFloat().toString(),
        randHex().toString(),
      ])
      break
    case Fields.Date:
      return rand([...initialValues, getDate()])
      break
    case Fields.Boolean:
      return rand([
        ...initialValues,
        sentenceCase(
          (excludeValue === 'True' ? false : randBoolean()).toString(),
        ),
      ])
      break
    case Fields.Tags:
      return rand([...initialValues, getTags()])
      break
    case Fields.Notes:
      return rand([
        ...initialValues,
        sentenceCase(randParagraph({ maxCharCount: 255 })),
      ])
      break
    default:
      return rand([
        ...initialValues,
        sentenceCase(randPhrase({ maxCharCount: 60 })),
      ])
      break
  }
}

export const factory = Factory.extend<RevisionLogData>({
  timestamp() {
    return randPastDate().toISOString()
  },
  user() {
    return randFullName()
  },
  modified_fields(): string[] {
    const modifiedFieldsCount = randNumber({ min: 1, max: 10 })
    const fields = [
      ...Object.values(Fields),
      sentenceCase(
        randWord({ length: randNumber({ min: 3, max: 10 }) }).join(' '),
      ),
    ]

    // Custom implementation for selecting multiple random elements
    const shuffled = [...fields].sort(() => 0.5 - safeMathRandom())
    return shuffled.slice(0, modifiedFieldsCount)
  },
  previous_values() {
    // Using the `modified_fields` array to generate an array with the same length
    return (this.modified_fields as string[]).map((field) =>
      generateValue(field),
    )
  },
  new_values() {
    // Using the `modified_fields` array to generate an array with the same length
    return (this.modified_fields as string[]).map((field, index) => {
      const previousValues = (this.previous_values as Array<string | null>)[
        index
      ]

      if (previousValues === null) {
        return generateValue(field, {
          allowNull: false,
        })
      }

      return generateValue(field, { excludeValue: previousValues })
    })
  },
  afterCreate(revisionLog, server) {
    const departments = server.schema.all('department') as Collection<
      ModelInstance<DepartmentData>
    >

    if (!revisionLog.department) {
      const department = rand(departments.models.map((d) => d.attrs.name))
      revisionLog.update({ active_department: department })
    }
  },
})

function generateRevisionLogsResponse(path: string, server: MockServer) {
  server.get(
    path,
    function (this: MirageServer, schema, request) {
      const isClientLog = request.url.includes('/clients/')
      const isPatientLog = request.url.includes('/patients/')
      let entity
      let logs = schema.all('revisionLog')

      if (isClientLog) {
        entity = schema.find('client', request.params.id)
      } else if (isPatientLog) {
        entity = schema.find('patient', request.params.id)
      }

      // return an empty log if the client/patient can't be found
      if (!entity) {
        // emptying the response without alter the original data held by mirage
        logs = logs.filter(() => false)
      }
      const sorted = sortable(logs, request)
      const { data, meta } = pageable(sorted, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
    { seed: ['Client', 'Patient'] },
  )
}

export function routes(server: MockServer) {
  generateRevisionLogsResponse('/clients/:id/revision_log', server)

  generateRevisionLogsResponse('/patients/:id/revision_log', server)
}

export function seeds(server: MockServer) {
  server.createList('revisionLog', 50)
}
